<script>
// components
import NextButton from 'dashboard/components-next/button/Button.vue';

// composables
import { useDebtStateFeedback } from 'dashboard/composables/useDebtStateFeedback';

// utils & constants
import { LocalStorage } from 'shared/helpers/localStorage';
import { LOCAL_STORAGE_KEYS } from 'dashboard/constants/localStorage';

// Dismissal is meant to stick: once an agent dismisses the strip for a
// conversation it should not reappear next session. Override the helper's
// 24h default with a long horizon (365 days, in ms).
const DISMISSAL_EXPIRY_MS = 365 * 24 * 60 * 60 * 1000;

export default {
  name: 'DebtStateStrip',
  components: {
    NextButton,
  },
  setup() {
    return useDebtStateFeedback();
  },
  data() {
    return {
      isDismissed: false,
    };
  },
  computed: {
    shouldShow() {
      return this.hasState && !this.isDismissed;
    },
  },
  watch: {
    conversationId: {
      immediate: true,
      handler() {
        this.resetEdit();
        this.isDismissed = this.isConversationDismissed();
      },
    },
  },
  methods: {
    isConversationDismissed() {
      if (!this.accountId || !this.conversationId) return false;
      return LocalStorage.getFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_DEBT_STATE_WIDGET,
        this.accountId,
        this.conversationId
      );
    },
    persistDismissal() {
      LocalStorage.setFlag(
        LOCAL_STORAGE_KEYS.DISMISSED_DEBT_STATE_WIDGET,
        this.accountId,
        this.conversationId,
        DISMISSAL_EXPIRY_MS
      );
      this.isDismissed = true;
    },
    dismiss() {
      this.persistDismissal();
    },
    async onConfirm() {
      const ok = await this.confirm();
      if (ok) this.persistDismissal();
    },
    async onSubmitCorrect() {
      const ok = await this.submitCorrect();
      if (ok) this.persistDismissal();
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <li v-if="shouldShow" class="debt-state-strip right list-none">
    <div class="debt-state-strip--container">
      <div class="debt-state-strip--summary">
        <h6 class="debt-state-strip--title">
          {{ $t('CONVERSATION.DEBT_STATE_WIDGET.TITLE') }}
        </h6>
        <div class="debt-state-strip--meta">
          <span class="debt-state-strip--label">
            {{ $t('CONVERSATION.DEBT_STATE_WIDGET.PREDICTED_LABEL') }}:
          </span>
          <span class="debt-state-strip--value">{{ stateLabel }}</span>
          <template v-if="confidenceLabel">
            <span class="debt-state-strip--label">
              {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CONFIDENCE_LABEL') }}:
            </span>
            <span class="debt-state-strip--value">{{ confidenceLabel }}</span>
          </template>
          <span v-if="sourceLabel" class="debt-state-strip--source">
            {{ sourceLabel }}
          </span>
        </div>
      </div>

      <div v-if="!isCorrecting" class="debt-state-strip--actions">
        <NextButton
          xs
          faded
          color="blue"
          :disabled="isSubmitting"
          :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CONFIRM')"
          @click="onConfirm"
        />
        <NextButton
          xs
          faded
          color="slate"
          :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT')"
          @click="startCorrect"
        />
        <NextButton
          xs
          ghost
          color="slate"
          :label="$t('CONVERSATION.DEBT_STATE_WIDGET.DISMISS')"
          @click="dismiss"
        />
      </div>

      <div v-else class="debt-state-strip--correct">
        <label class="debt-state-strip--label" :for="'debt-state-select'">
          {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT_PROMPT') }}
        </label>
        <select
          id="debt-state-select"
          v-model="selectedState"
          class="debt-state-strip--select"
        >
          <option value="" disabled>
            {{ $t('CONVERSATION.DEBT_STATE_WIDGET.CORRECT_PROMPT') }}
          </option>
          <option v-for="state in debtStates" :key="state" :value="state">
            {{ stateOptionLabel(state) }}
          </option>
        </select>
        <div class="debt-state-strip--actions">
          <NextButton
            xs
            faded
            color="blue"
            :disabled="!selectedState || isSubmitting"
            :label="$t('CONVERSATION.DEBT_STATE_WIDGET.SAVE')"
            @click="onSubmitCorrect"
          />
          <NextButton
            xs
            ghost
            color="slate"
            :label="$t('CONVERSATION.DEBT_STATE_WIDGET.CANCEL')"
            @click="cancelCorrect"
          />
        </div>
      </div>
    </div>
  </li>
</template>

<style scoped lang="scss">
.debt-state-strip {
  @apply flex flex-row justify-end mt-4;

  .debt-state-strip--container {
    @apply flex flex-col gap-1.5 max-w-[320px] items-end;
  }

  .debt-state-strip--title {
    @apply text-n-slate-11 mt-0.5 text-xxs;
  }

  .debt-state-strip--meta {
    @apply flex flex-wrap items-center gap-1 text-xs text-n-slate-12;
  }

  .debt-state-strip--label {
    @apply text-n-slate-11;
  }

  .debt-state-strip--value {
    @apply font-medium;
  }

  .debt-state-strip--source {
    @apply text-n-slate-10 text-xxs;
  }

  .debt-state-strip--actions {
    @apply flex items-center gap-1;
  }

  .debt-state-strip--correct {
    @apply flex flex-col items-end gap-1;
  }

  .debt-state-strip--select {
    @apply text-xs rounded-md border border-n-weak bg-n-background py-1 px-2;
  }
}
</style>
